import { useQuery } from '@apollo/client';

import { PET_CLINICAL_SUMMARY_QUERY } from './clinical.gql';

import { VETERINARIAN_USERS_QUERY } from '@/modules/appointments/appointments.gql';
import { CUSTOMER_QUERY } from '@/modules/customers-pets/customers.gql';
import { PET_QUERY } from '@/modules/customers-pets/pets.gql';

interface PetShape {
  id: string;
  customer_id: string;
  name: string;
  species: string | null;
  breed: string | null;
  color: string | null;
  sex: string | null;
  birth_date: string | null;
  approximate_age_months: number | null;
  weight_grams: number | null;
}

interface CustomerShape {
  id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
}

interface VeterinarianShape {
  id: string;
  full_name: string;
}

interface PetClinicalSummaryShape {
  pet_name: string;
  species: string | null;
  pet_record_code: string | null;
}

/**
 * Header data shared by every printed clinical document (receta + expediente). Clinic
 * branding (name/logo/color/contact) is a SEPARATE concern — see `useClinicIdentity()` —
 * this hook only resolves patient/owner/attending-vet data.
 */
export interface RxPrintBaseResolved {
  patient_name: string;
  species: string | null;
  breed: string | null;
  color: string | null;
  sex: string | null;
  age_label: string | null;
  weight_label: string | null;
  owner_name: string;
  owner_phone: string | null;
  pet_record_code: string | null;
  vet_name: string;
  visit_folio: string;
}

function ageLabel(pet: PetShape | null): string | null {
  if (!pet) return null;
  if (pet.birth_date) {
    const birth = new Date(pet.birth_date);
    if (!Number.isNaN(birth.getTime())) {
      const now = new Date();
      let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
      if (now.getDate() < birth.getDate()) months -= 1;
      if (months < 0) return null;
      const years = Math.floor(months / 12);
      const remaining_months = months % 12;
      if (years <= 0) return `${remaining_months} m`;
      return remaining_months > 0 ? `${years} a ${remaining_months} m` : `${years} a`;
    }
  }
  if (pet.approximate_age_months != null) return `${pet.approximate_age_months} m (aprox.)`;
  return null;
}

function weightLabel(pet: PetShape | null): string | null {
  if (!pet?.weight_grams) return null;
  return `${(pet.weight_grams / 1000).toFixed(2)} kg`;
}

/**
 * Resolves patient/owner/attending-vet data for a printed document, once per screen. The
 * attending vet is looked up by `visit.veterinarian_id` — never the currently logged-in
 * session user — so a reprint from history always shows the doctor who actually attended.
 */
export function useRxPrintBase(args: {
  pet_id: string | undefined | null;
  visit: { veterinarian_id: string | null; folio_number: string | null } | null;
}): RxPrintBaseResolved {
  const pet_id = args.pet_id ?? undefined;

  const { data: petData } = useQuery<{ pet: PetShape }>(PET_QUERY, {
    variables: { id: pet_id ?? '' },
    skip: !pet_id,
    fetchPolicy: 'cache-first',
  });
  const pet = petData?.pet ?? null;

  const { data: customerData } = useQuery<{ customer: CustomerShape }>(CUSTOMER_QUERY, {
    variables: { id: pet?.customer_id ?? '' },
    skip: !pet?.customer_id,
    fetchPolicy: 'cache-first',
  });
  const customer = customerData?.customer ?? null;

  const { data: summaryData } = useQuery<{ petClinicalSummary: PetClinicalSummaryShape | null }>(
    PET_CLINICAL_SUMMARY_QUERY,
    {
      variables: { pet_id: pet_id ?? '' },
      skip: !pet_id,
      fetchPolicy: 'cache-first',
    },
  );
  const summary = summaryData?.petClinicalSummary ?? null;

  const { data: vetsData } = useQuery<{ veterinarianUsers: VeterinarianShape[] }>(
    VETERINARIAN_USERS_QUERY,
    { fetchPolicy: 'cache-first' },
  );
  const vets = vetsData?.veterinarianUsers ?? [];
  const vet = vets.find((v) => v.id === args.visit?.veterinarian_id);

  const owner_name = customer ? `${customer.first_name} ${customer.last_name}`.trim() : '—';

  return {
    patient_name: pet?.name ?? summary?.pet_name ?? '—',
    species: pet?.species ?? summary?.species ?? null,
    breed: pet?.breed ?? null,
    color: pet?.color ?? null,
    sex: pet?.sex ?? null,
    age_label: ageLabel(pet),
    weight_label: weightLabel(pet),
    owner_name,
    owner_phone: customer?.phone ?? null,
    pet_record_code: summary?.pet_record_code ?? null,
    vet_name: vet?.full_name ?? '—',
    visit_folio: args.visit?.folio_number ?? '—',
  };
}
